import React, { useEffect, useState } from "react";
import { NotifyConfirm } from "../../../../components/notify/Notify";
import { path } from "../../../../constants/path";
import {
  createFLFContext,
  getFLFContext,
  removeFLFContext,
  updateFLFContext,
} from "../../../../apis/FLF/context.api";
import { CACHE_CONTEXT } from "../../../../constants/actionCache";
import useCurrentActionCache from "../../../../hooks/useCurrentActionCache";
import { useGetPermissionAccessControl } from "../../../../context/PermissionAccessControl";
import ControlPanelSystem from "src/components/controlPanelSystem/ControlPanelSystem";
import { useCallback } from "react";
import useControlPanel from "src/hooks/useControlPanel";
import { useMemo } from "react";
import Pagination from "src/components/Pagination/Pagination";
import FLFContextControlPanel from "../../components/ControlPanel/FLFContextControlPanel";
import ContextTable from "../../components/Table/ContextTable";
import { dataStateType } from "src/constants/status";

const initContextData = {
  list: [],
  total_record: 0,
};

const FLFContext = () => {
  const {
    page,
    searchKey,
    numberPage,
    setNumberPage,
    limit,
    setLimit,
    status,
    setStatus,
    startIndex,
    endIndex,
    setStartDate,
    setEndDate,
    handleInputChange,
    handleChangePage,
  } = useControlPanel();
  // cache
  const permissionsAccessControl = useGetPermissionAccessControl();
  const { currentActionCache, setCurrentActionCache } = useCurrentActionCache();
  const [contextData, setContextData] = useState(initContextData);
  const [filterContexts, setFilterContexts] = useState([]);
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [errorsForm, setErrorsForm] = useState({});

  const fetchContexts = useCallback(() => {
    getFLFContext({}, (result) => {
      setContextData({ ...result });
      setFilterContexts([...result.list]);
    });
  }, []);

  useEffect(() => {
    fetchContexts();
  }, [fetchContexts]);

  useEffect(() => {
    setNumberPage(page);
  }, [page, setNumberPage]);

  const updateFilteredContext = useCallback((contexts) => {
    setFilterContexts(contexts);
  }, []);

  /* ---- handle search ----- */
  const searchByKeyWord = useCallback(
    (contexts, key) => {
      if (status === dataStateType.INACTIVE) {
        return [];
      }
      return contexts.filter((context) => {
        return context.name.toLowerCase().includes(key.toLowerCase().trim());
      });
    },
    [status]
  );

  const handleSearch = useCallback(
    (value) => {
      const filteredContext = value
        ? searchByKeyWord(contextData.list, value)
        : contextData.list;
      updateFilteredContext(filteredContext);
    },
    [contextData.list, searchByKeyWord, updateFilteredContext]
  );

  useEffect(() => {
    handleSearch(searchKey);
  }, [searchKey, handleSearch]);

  /* ---- handle add new and update ----- */
  const addOrEdit = useCallback(
    (context, resetForm) => {
      if (context.id) {
        updateFLFContext(context.id, context, handleApiResponse);
      } else {
        createFLFContext(context, handleApiResponse);
      }

      function handleApiResponse(result) {
        if (result.data?.status === 422) {
          setErrorsForm(result.data.data);
        } else {
          fetchContexts();
          resetForm();
          setRecordForEdit(null);
          setOpenPopup(false);
          setErrorsForm({});
          setCurrentActionCache(CACHE_CONTEXT);
        }
      }
    },
    [fetchContexts, setCurrentActionCache]
  );

  /* ---- handle delete ----- */

  const handleDelete = (contextDeleted) => {
    NotifyConfirm("warning", "Are you delete it ?", (result) => {
      if (result.isConfirmed) {
        removeFLFContext(contextDeleted.id, () => {
          setCurrentActionCache(CACHE_CONTEXT);
          fetchContexts();
        });
      }
    });
  };

  const handleOpenPopup = useCallback((isOpen, item = null) => {
    setOpenPopup(isOpen);
    setRecordForEdit(item);
    setErrorsForm({});
  }, []);

  /* ---- result for render ----- */
  const resultFilter = useMemo(
    () => searchByKeyWord(filterContexts, searchKey),
    [filterContexts, searchByKeyWord, searchKey]
  );
  const listItem = useMemo(
    () => resultFilter.slice(startIndex, endIndex),
    [resultFilter, startIndex, endIndex]
  );

  return (
    <div>
      <div className="page-header">
        <h2>{"FLF Context"}</h2>
      </div>

      <FLFContextControlPanel
        handleOpenPopup={handleOpenPopup}
        currentActionCache={currentActionCache}
        recordForEdit={recordForEdit}
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
        addOrEdit={addOrEdit}
        errorsForm={errorsForm}
      >
        <ControlPanelSystem
          searchKey={searchKey}
          handleInputChange={(e) => handleInputChange(e, handleSearch)}
          limit={limit}
          setLimit={setLimit}
          setNumberPage={setNumberPage}
          status={status}
          setStatus={setStatus}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          isDisplayDatePicker={false}
        />
      </FLFContextControlPanel>

      <ContextTable
        permissionsAccessControl={permissionsAccessControl}
        data={listItem}
        handleOpenPopup={handleOpenPopup}
        handleDelete={handleDelete}
        status={status}
      />

      <Pagination
        pathname={path.flfContext}
        page={numberPage}
        handleChangePage={(page) => handleChangePage(page)}
        limit={limit}
        pageSize={resultFilter.length}
      />
    </div>
  );
};

export default FLFContext;
